/**
 * Slug helpers for SEO-friendly URLs.
 * Pattern: `<lowercase-dashed-text>-<numeric-id>` (e.g. "senior-python-engineer-at-talix-labs-bengaluru-12345").
 * The trailing numeric ID is the stable resolution key; the prefix is purely cosmetic for SEO and humans.
 *
 * IMPORTANT: any change to slugify behaviour here must be mirrored in backend/utils/slug.py
 * so frontend-generated links match backend-rendered canonical forms.
 */

const STOPWORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'is', 'of',
  'on', 'or', 'the', 'to', 'with',
]);

/** Lowercase, ASCII-only, dash-separated. Strips stopwords. Caps total length at 80 chars. */
export function slugify(input: string, maxLength = 80): string {
  if (!input) return '';
  const ascii = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = ascii.split(' ').filter((w) => w && /[a-z0-9]/.test(w) && !STOPWORDS.has(w));
  // Collapse any internal runs of dashes left over from punctuation handling.
  const joined = words.join('-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  if (joined.length <= maxLength) return joined;
  // truncate without cutting a word mid-way when possible
  const cut = joined.slice(0, maxLength);
  const lastDash = cut.lastIndexOf('-');
  return lastDash > maxLength * 0.6 ? cut.slice(0, lastDash) : cut;
}

interface SlugJobInput {
  id: number;
  title: string;
  company?: { name?: string | null } | null;
  location?: string | null;
}

/** Build the full canonical slug for a job: "<slugified-prefix>-<id>". */
export function jobSlug(job: SlugJobInput): string {
  const parts: string[] = [];
  if (job.title) parts.push(job.title);
  if (job.company?.name) parts.push(`at ${job.company.name}`);
  if (job.location) parts.push(`in ${job.location}`);
  const prefix = slugify(parts.join(' '), 70);
  return prefix ? `${prefix}-${job.id}` : String(job.id);
}

/** Full path for a job detail page including leading slash. */
export function jobUrl(job: SlugJobInput): string {
  return `/jobs/${jobSlug(job)}`;
}

/**
 * Extract the numeric ID from a URL segment. Accepts either:
 *   - A pure numeric string ("12345") — backward compat for legacy links
 *   - A slug ending with "-<id>" ("senior-python-engineer-12345")
 * Returns null when neither form matches.
 */
export function parseJobIdFromSlug(slugOrId: string | undefined | null): number | null {
  if (!slugOrId) return null;
  if (/^\d+$/.test(slugOrId)) {
    const n = parseInt(slugOrId, 10);
    return Number.isFinite(n) ? n : null;
  }
  const m = slugOrId.match(/-(\d+)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/** Build a Company URL: "/companies/<slug>-<id>". */
export function companyUrl(company: { id: number; name: string }): string {
  const prefix = slugify(company.name, 60);
  return `/companies/${prefix ? `${prefix}-${company.id}` : company.id}`;
}
